import { useState, useCallback, useEffect } from "react";
import { Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import ContractLayout from "./components/ContractLayout";
import StepWizard from "./components/StepWizard";
import SEOHead from "./components/SEOHead";
import inchiriereConfig from "./contracts/inchiriere";
import comodatConfig from "./contracts/comodat";
import imprumutConfig from "./contracts/imprumut";
import vanzareAutoConfig from "./contracts/vanzare-auto";
import actAditionalInchiriereConfig from "./contracts/act-aditional-inchiriere";
import notificareReziliereConfig from "./contracts/notificare-reziliere";
import prestariServiciiConfig from "./contracts/prestari-servicii";
import colaborarePFAConfig from "./contracts/colaborare-pfa";
import ndaConfig from "./contracts/nda";
import useIsMobile from "./hooks/useIsMobile";
import NavBar from "./components/NavBar";
import BlogListPage from "./blog/BlogListPage";
import BlogArticlePage from "./blog/BlogArticlePage";
import ModelLandingPage from "./landing/ModelLandingPage";
import CONTRACT_SLUGS from "./utils/contractSlugs";
import {
  trackSelectContract,
  trackFlowChoiceViewed,
  trackFlowChoiceClicked,
  trackModelPdfDownload,
} from "./utils/analytics";

const CONTRACT_TYPES = [
  {
    id: "inchiriere",
    category: "pf",
    slug: CONTRACT_SLUGS["inchiriere"],
    title: "Contract de Inchiriere",
    description: "Genereaza un contract de inchiriere complet, cu proces verbal de predare-primire.",
    seoTitle: "Model Contract de Închiriere 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract de închiriere personalizat în 2 minute, cu CNP și IBAN validate automat. Sau descarcă modelul PDF gol — gratuit, fără cont.",
    seoIntro: "Creează un contract de închiriere – model 2026 – în format PDF, complet și actualizat conform Codului Civil din România. Formularul include datele proprietarului și chiriașului, descrierea imobilului, durata, chiria lunară și clauzele esențiale. La final primești și procesul-verbal de predare-primire. Completezi pas cu pas, descarci PDF-ul și îl poți printa — gratuit, fără cont.",
    choiceIntro: "Modelele PDF de pe alte site-uri îți cer să completezi manual 20+ câmpuri, să verifici singur CNP-ul, IBAN-ul și sumele scrise în litere — iar o singură greșeală poate invalida contractul. Generatorul nostru face toată munca în 2 minute, cu validare automată și PDF final gata de semnat. Sau, dacă preferi varianta clasică, descarci modelul PDF gol și-l completezi cu pixul.",
    bonusBenefit: "Proces-verbal predare-primire generat automat",
    icon: "\uD83C\uDFE0",
    available: true,
    config: inchiriereConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un contract de inchiriere?",
          content: "Contractul de inchiriere (sau contractul de locatiune) este un acord legal prin care proprietarul (locatorul) cedeaza folosinta unui imobil chiriasului (locatarului), in schimbul unei chirii lunare. Este reglementat de art. 1777-1850 din Codul Civil si poate fi incheiat in forma scrisa, fara a fi necesara autentificarea la notar. Acest model de contract de inchiriere 2026 este actualizat conform legislatiei in vigoare din Romania si poate fi utilizat pentru apartamente, case sau alte spatii locative.",
        },
        {
          title: "Ce trebuie sa contina un contract de inchiriere?",
          content: [
            "Datele complete ale proprietarului si chiriasului (nume, CNP, adresa, serie CI)",
            "Descrierea detaliata a imobilului (adresa, numar camere, suprafata)",
            "Durata contractului (data inceput si data sfarsit)",
            "Valoarea chiriei lunare si modalitatea de plata",
            "Valoarea garantiei (depozit de garantie)",
            "Obligatiile proprietarului si ale chiriasului",
            "Conditii de reziliere si de predare a imobilului",
            "Proces verbal de predare-primire",
          ],
        },
        {
          title: "Trebuie autentificat la notar contractul de inchiriere?",
          content: "Nu, contractul de inchiriere nu trebuie obligatoriu autentificat la notar pentru a fi valid. Un contract semnat de ambele parti este suficient din punct de vedere legal. Totusi, autentificarea notariala ofera un avantaj important: contractul devine titlu executoriu, ceea ce inseamna ca proprietarul poate solicita evacuarea chiriasului direct prin executor judecatoresc, fara a mai fi nevoie de un proces in instanta.",
        },
        {
          title: "Cum descarci contractul de inchiriere in format PDF?",
          content: "Completeaza formularul de pe aceasta pagina pas cu pas: introdu datele proprietarului, datele chiriasului, informatiile despre imobil, chiria lunara si durata contractului. La final, apasa butonul \"Descarca PDF\" si vei primi documentul complet, gata de printat si semnat. Generatorul creeaza automat si procesul verbal de predare-primire.",
        },
      ],
      faq: [
        {
          q: "Cat dureaza un contract de inchiriere?",
          a: "Durata este stabilita de comun acord intre parti. Cel mai frecvent, contractele de inchiriere se incheie pe o perioada de 1 an, cu posibilitatea de prelungire. Legea nu impune o durata minima sau maxima.",
        },
        {
          q: "Ce se intampla daca chiriasul nu plateste chiria?",
          a: "Proprietarul poate solicita rezilierea contractului si evacuarea chiriasului. Daca contractul este autentificat la notar, evacuarea se poate face direct prin executor judecatoresc. In caz contrar, proprietarul trebuie sa obtina o hotarare judecatoreasca.",
        },
        {
          q: "Trebuie inregistrat contractul la ANAF?",
          a: "Da, proprietarul are obligatia de a inregistra contractul de inchiriere la Administratia Financiara (ANAF) in termen de 30 de zile de la incheierea acestuia. Veniturile din chirii sunt impozabile conform Codului Fiscal.",
        },
      ],
    },
  },
  {
    id: "act-aditional-inchiriere",
    category: "pf",
    slug: CONTRACT_SLUGS["act-aditional-inchiriere"],
    title: "Act Aditional Inchiriere",
    description: "Act aditional pentru prelungirea contractului sau modificarea chiriei.",
    seoTitle: "Model Act Adițional Închiriere 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează act adițional la contractul de închiriere în 2 minute — prelungire, modificare chirie sau clauze. PDF gratuit, fără cont.",
    seoIntro: "Generează gratuit un act adițional la contractul de închiriere – model 2026 – în format PDF. Prelungire contract, modificare chirie sau alte clauze. Completezi datele, descarci PDF-ul gata de semnat, fără cont.",
    choiceIntro: "Un act adițional scris de mână pe un PDF gol riscă să omită referința corectă la contractul original sau să conțină erori în datele părților. Generatorul nostru completează automat toate referințele și validează datele — în 2 minute ai PDF-ul gata de semnat. Sau descarci modelul PDF gol.",
    bonusBenefit: "Referințe la contractul original completate automat",
    icon: "\uD83D\uDCDD",
    available: true,
    config: actAditionalInchiriereConfig,
    seoContent: {
      sections: [],
      faq: [
        {
          q: "Trebuie inregistrat actul aditional la ANAF?",
          a: "Da, orice modificare a contractului (inclusiv prelungirea sau modificarea chiriei) trebuie comunicata ANAF prin depunerea unui nou formular C168 sau prin actualizarea declaratiei existente, in termen de 30 de zile."
        },
        {
          q: "Trebuie autentificat la notar?",
          a: "Nu, actul aditional are aceeasi forma ca si contractul original. Daca contractul original nu a fost autentificat, nici actul aditional nu trebuie. Daca a fost autentificat, se recomanda autentificarea si a actului aditional."
        },
        {
          q: "Pot modifica mai multe clauze simultan?",
          a: "Da, un act aditional poate modifica oricâte clauze din contractul original."
        },
        {
          q: "Ce se intampla daca nu fac act aditional la prelungire?",
          a: "Daca contractul expira si chiriasul continua sa foloseasca imobilul cu acordul tacit al proprietarului, contractul se considera prelungit automat (tacita relocatiune) pe durata nedeterminata. Totusi, e recomandat un act aditional scris pentru claritate."
        },
      ],
    },
  },
  {
    id: "imprumut",
    category: "pf",
    slug: CONTRACT_SLUGS["imprumut"],
    title: "Contract de Imprumut",
    description: "Contract de imprumut intre persoane fizice, conform art. 2158-2170 Cod Civil.",
    seoTitle: "Model Contract de Împrumut 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract de împrumut între persoane fizice în 2 minute, cu sume validate automat. PDF gratuit, conform Codului Civil.",
    seoIntro: "Generează un contract de împrumut între persoane fizice – model 2026 – în format PDF, redactat conform art. 2158–2170 din Codul Civil. Ideal pentru împrumuturi de bani între familie, prieteni sau cunoscuți. Include suma împrumutată, termenul de restituire, dobânda (dacă este cazul) și datele complete ale ambelor părți. Descarci documentul imediat, gratuit, fără cont.",
    choiceIntro: "Într-un contract de împrumut completat de mână, o singură cifră greșită la sumă sau la dobândă poate crea dispute între părți. Generatorul nostru scrie automat sumele în litere, calculează dobânda și validează CNP-ul — în 2 minute ai contractul gata. Sau descarci modelul PDF gol.",
    bonusBenefit: "Sume scrise automat în litere, dobândă calculată",
    icon: "\uD83D\uDCB0",
    available: true,
    config: imprumutConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un contract de imprumut intre persoane fizice?",
          content: "Contractul de imprumut (sau imprumutul de consumatie) este reglementat de art. 2158-2170 din Codul Civil. Prin acest contract, o persoana (imprumutator) remite altei persoane (imprumutat) o suma de bani sau alte bunuri fungibile, cu obligatia ca imprumutatul sa restituie aceeasi suma sau cantitate de bunuri de aceeasi natura si calitate, la termenul stabilit. Acest model de contract de imprumut 2026 este actualizat conform legislatiei in vigoare din Romania si poate fi utilizat pentru imprumuturi de bani intre familie, prieteni sau cunoscuti.",
        },
        {
          title: "Ce elemente obligatorii trebuie sa contina contractul de imprumut?",
          content: [
            "Datele complete ale imprumutatorului si imprumutatului (nume, CNP, adresa, serie CI)",
            "Suma imprumutata, in cifre si litere",
            "Modalitatea de predare a sumei (numerar, transfer bancar)",
            "Termenul de restituire (data exacta sau numar de luni)",
            "Dobanda aplicata (daca este cazul) sau mentiunea ca imprumutul este fara dobanda",
            "Graficul de rambursare (daca se restituie in rate)",
            "Penalitati de intarziere (optional)",
            "Semnaturile ambelor parti",
          ],
        },
        {
          title: "Se poate percepe dobanda la un imprumut intre persoane fizice?",
          content: "Da, Codul Civil permite stabilirea unei dobanzi pentru imprumutul intre persoane fizice. Daca partile nu mentioneaza nimic despre dobanda, imprumutul se considera gratuit. Dobanda poate fi fixa sau variabila, dar nu trebuie sa depaseasca dobanda legala penalizatoare (publicata de BNR). Veniturile din dobanzi sunt impozabile conform Codului Fiscal.",
        },
        {
          title: "Care este diferenta fata de un credit bancar?",
          content: "Spre deosebire de creditul bancar, imprumutul intre persoane fizice nu necesita evaluarea bonabilitatii, nu implica garantii ipotecare si nu apare in bazele de date ale Biroului de Credit. De asemenea, nu exista comisioane de acordare sau administrare. Contractul de imprumut intre persoane fizice este mai flexibil si mai rapid, fiind ideal pentru sume mici si medii imprumutate intre familie sau prieteni.",
        },
      ],
      faq: [
        {
          q: "Este obligatoriu contractul scris pentru imprumut?",
          a: "Legea prevede ca imprumutul poate fi dovedit si prin alte mijloace de proba, dar forma scrisa este puternic recomandata. Un contract scris protejeaza ambele parti si constituie o dovada clara a sumei imprumutate, a termenului de restituire si a conditiilor stabilite.",
        },
        {
          q: "Ce se intampla daca imprumutatul nu restituie banii?",
          a: "Imprumutantul poate solicita restituirea sumei pe cale amiabila sau prin actiune in instanta. Contractul de imprumut in forma scrisa constituie o proba solida in fata instantei. Termenul de prescriptie pentru recuperarea sumei este de 3 ani de la data scadentei.",
        },
        {
          q: "Trebuie declarat imprumutul la ANAF?",
          a: "Imprumutul in sine nu trebuie declarat la ANAF, dar daca se percepe dobanda, veniturile din dobanzi sunt impozabile si trebuie declarate. De asemenea, sumele mari transferate intre persoane fizice pot fi verificate de ANAF in cadrul controalelor de rutina.",
        },
      ],
    },
  },
  {
    id: "vanzare-auto",
    category: "pf",
    slug: CONTRACT_SLUGS["vanzare-auto"],
    title: "Vanzare-Cumparare Auto",
    description: "Contract de vanzare-cumparare auto, conform Codului Civil.",
    seoTitle: "Model Contract Vânzare-Cumpărare Auto 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract vânzare auto personalizat în 2 minute, cu serie șasiu și preț validate. PDF gratuit, acceptat la înmatriculări.",
    seoIntro: "Generează un contract de vânzare-cumpărare auto model 2026, în format PDF, conform Codului Civil, pregătit pentru înmatriculare. Formularul include datele vânzătorului și cumpărătorului, informațiile complete despre vehicul (marcă, model, an fabricație, serie șasiu VIN, număr de înmatriculare), prețul și modalitatea de plată. Descarci imediat documentul complet, gratuit, gata de semnat.",
    choiceIntro: "Un contract de vânzare auto completat greșit poate bloca înmatricularea la RAR. Generatorul nostru validează seria de șasiu, scrie prețul în litere automat și generează un document acceptat la Serviciul de Înmatriculări — în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Preț scris automat în litere, acceptat la înmatriculări",
    icon: "\uD83D\uDE97",
    available: true,
    config: vanzareAutoConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este contractul de vanzare-cumparare auto?",
          content: "Contractul de vanzare-cumparare auto este documentul legal prin care proprietarul unui vehicul (vanzatorul) transfera dreptul de proprietate asupra autoturismului catre cumparator, in schimbul unui pret. Este reglementat de Codul Civil si este obligatoriu pentru inmatricularea vehiculului pe numele noului proprietar la Serviciul de Inmatriculari. Acest model de contract de vanzare-cumparare auto 2026 este actualizat conform legislatiei in vigoare din Romania si poate fi utilizat pentru autoturisme, motociclete sau alte autovehicule.",
        },
        {
          title: "Ce acte sunt necesare pentru vanzarea unui autoturism?",
          content: [
            "Cartea de identitate a vehiculului (CIV) in original",
            "Certificatul de inmatriculare in original",
            "Actele de identitate ale vanzatorului si cumparatorului (CI/pasaport)",
            "Contractul de vanzare-cumparare completat si semnat de ambele parti",
            "Fiscalul (certificat de atestare fiscala) de la Primaria unde este inregistrat vehiculul",
            "Asigurarea RCA valabila (obligatorie pentru circulatie)",
            "Inspectia Tehnica Periodica (ITP) valabila",
          ],
        },
        {
          title: "Ce urmeaza dupa semnarea contractului de vanzare-cumparare auto?",
          content: "Dupa semnarea contractului, cumparatorul trebuie sa inscrie autovehiculul pe numele sau la Serviciul de Inmatriculari in termen de 30 de zile. Vanzatorul trebuie sa radieze vehiculul de la Primaria unde era inregistrat fiscal. Cumparatorul va plati taxele de inmatriculare si va obtine un nou certificat de inmatriculare si placute noi (daca este cazul).",
        },
        {
          title: "Cum descarci contractul de vanzare-cumparare auto in format PDF?",
          content: "Completeaza formularul de mai sus cu datele vanzatorului, datele cumparatorului, informatiile despre vehicul (marca, model, an fabricatie, serie sasiu, numar de inmatriculare) si pretul de vanzare. Apasa \"Descarca PDF\" si primesti documentul complet, gata de printat si semnat. Contractul este conform cu cerintele Serviciului de Inmatriculari.",
        },
      ],
      faq: [
        {
          q: "Trebuie autentificat la notar contractul de vanzare-cumparare auto?",
          a: "Nu, contractul de vanzare-cumparare auto nu necesita autentificare notariala. Un contract semnat de ambele parti este suficient si acceptat de Serviciul de Inmatriculari. Totusi, unii cumparatori prefera autentificarea pentru securitate suplimentara.",
        },
        {
          q: "In cat timp trebuie inmatriculat vehiculul dupa cumparare?",
          a: "Cumparatorul are obligatia de a inmatricula vehiculul pe numele sau in termen de 30 de zile de la data semnarii contractului. Nerespectarea acestui termen poate atrage amenzi contraventionale.",
        },
        {
          q: "Se poate vinde o masina fara ITP valabil?",
          a: "Da, vehiculul poate fi vandut fara ITP valabil, dar nu poate circula pe drumurile publice fara ITP. Cumparatorul va trebui sa efectueze ITP-ul inainte de inmatriculare. Este recomandat sa mentionati in contract starea ITP-ului.",
        },
      ],
    },
  },
  {
    id: "comodat",
    category: "pf",
    slug: CONTRACT_SLUGS["comodat"],
    title: "Contract de Comodat",
    description: "Contract de comodat (folosinta gratuita) conform art. 2146-2157 Cod Civil.",
    seoTitle: "Model Contract de Comodat 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract de comodat personalizat în 2 minute, cu CNP validate automat. PDF gratuit, conform Codului Civil.",
    seoIntro: "Generează un contract de comodat model 2026 (împrumut de folosință gratuită), în format PDF, conform art. 2146–2157 din Codul Civil. Ideal atunci când dai în folosință gratuită un imobil, un autoturism sau alt bun. Formularul include datele părților, descrierea bunului, durata și obligațiile comodatarului. Descarci imediat documentul complet, gratuit, fără cont.",
    choiceIntro: "Un contract de comodat completat de mână pe un PDF gol riscă erori la CNP sau la descrierea bunului — iar fără un contract corect nu poți stabili sediul firmei sau domiciliul. Generatorul nostru validează totul automat în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Inventar bunuri și obligații comodatar incluse",
    icon: "\uD83E\uDD1D",
    available: true,
    config: comodatConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un contract de comodat?",
          content: "Contractul de comodat (imprumutul de folosinta) este reglementat de art. 2146-2157 din Codul Civil. Prin acest contract, o persoana (comodantul) remite un bun mobil sau imobil altei persoane (comodatarul) pentru a-l folosi temporar si gratuit, cu obligatia de a-l restitui la termen. Spre deosebire de inchiriere, comodatul este intotdeauna gratuit. Acest model de contract de comodat 2026 este actualizat conform legislatiei in vigoare din Romania si poate fi utilizat pentru imobile, autoturisme sau alte bunuri date in folosinta gratuita.",
        },
        {
          title: "Care sunt obligatiile comodatarului?",
          content: [
            "Sa foloseasca bunul conform destinatiei stabilite in contract",
            "Sa conserve bunul cu prudenta si diligenta unui bun proprietar",
            "Sa suporte cheltuielile de intretinere curenta ale bunului",
            "Sa nu cedeze folosinta bunului unei terte persoane fara acordul comodantului",
            "Sa restituie bunul la termenul convenit, in starea in care l-a primit",
            "Sa raspunda pentru deteriorarea sau pierderea bunului din culpa sa",
          ],
        },
        {
          title: "Care este diferenta dintre comodat si inchiriere?",
          content: "Diferenta fundamentala este ca comodatul este intotdeauna gratuit, in timp ce inchirierea presupune plata unei chirii. In cazul comodatului, comodatarul nu plateste nicio suma pentru folosinta bunului, dar suporta cheltuielile de intretinere curenta (utilitati, reparatii minore). Comodatul este frecvent folosit intre membri ai familiei sau in relatii de incredere, de exemplu cand un parinte da in folosinta gratuita un apartament copilului sau.",
        },
        {
          title: "Cand este necesar un contract de comodat?",
          content: "Contractul de comodat este necesar ori de cate ori dai in folosinta gratuita un bun, mai ales un imobil. Este util pentru stabilirea domiciliului sau resedintei la un imobil care nu iti apartine, pentru justificarea sediului social al unei firme (PFA, SRL) la o adresa pusa la dispozitie gratuit, si pentru clarificarea drepturilor si obligatiilor ambelor parti. Forma scrisa protejeaza atat comodantul, cat si comodatarul.",
        },
      ],
      faq: [
        {
          q: "Se poate incheia comodat pentru o masina?",
          a: "Da, contractul de comodat poate avea ca obiect orice bun — imobil (apartament, casa, teren) sau mobil (autoturism, echipamente). In cazul unui autoturism, comodatarul poate folosi masina gratuit, dar raspunde pentru intretinerea curenta si pentru eventualele daune produse din culpa sa.",
        },
        {
          q: "Trebuie inregistrat contractul de comodat la ANAF?",
          a: "Comodatul fiind gratuit, nu genereaza venituri impozabile, deci nu trebuie inregistrat la ANAF in scop fiscal. Totusi, daca contractul este folosit pentru stabilirea sediului unei firme, va trebui depus la Registrul Comertului.",
        },
        {
          q: "Cat timp poate dura un contract de comodat?",
          a: "Durata comodatului este stabilita de comun acord intre parti. Poate fi pe durata determinata (ex: 1 an) sau nedeterminata. In cazul duratei nedeterminate, comodantul poate solicita restituirea bunului oricand, cu un preaviz rezonabil.",
        },
      ],
    },
  },
  {
    id: "prestari-servicii",
    category: "firme",
    slug: CONTRACT_SLUGS["prestari-servicii"],
    title: "Contract Prestari Servicii",
    description: "Contract de prestari servicii conform Codului Civil, pentru PF, PFA sau SRL.",
    seoTitle: "Model Contract Prestări Servicii 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract de prestări servicii personalizat în 2 minute, cu clauze NDA și PI incluse. PDF gratuit, fără cont.",
    seoIntro: "Generează un contract de prestări servicii – model 2026 – în format PDF, conform art. 1851–1857 din Codul Civil. Ideal pentru freelanceri, PFA-uri, SRL-uri sau colaborări între persoane fizice. Formularul include datele părților (PF, PFA sau SRL), obiectul contractului, durata, prețul, obligațiile părților, clauze de confidențialitate și proprietate intelectuală. Descarci imediat, gratuit, fără cont.",
    choiceIntro: "Un contract de prestări servicii completat manual riscă să omită clauze esențiale de confidențialitate sau proprietate intelectuală — iar fără ele, ești expus juridic. Generatorul nostru include automat NDA, clauze PI și toate obligațiile conform Codului Civil — în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Clauze NDA și proprietate intelectuală incluse automat",
    icon: "\uD83D\uDCBC",
    available: true,
    config: prestariServiciiConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un contract de prestari servicii?",
          content: "Contractul de prestari servicii este un acord prin care o persoana (prestatorul) se obliga sa execute anumite servicii in favoarea altei persoane (beneficiarul), in schimbul unui pret. Este reglementat de art. 1851-1857 din Codul Civil (contractul de antrepriza) si poate fi incheiat intre persoane fizice, PFA-uri sau societati comerciale. Acest model de contract de prestari servicii 2026 este actualizat conform legislatiei in vigoare din Romania.",
        },
        {
          title: "Ce trebuie sa contina un contract de prestari servicii?",
          content: [
            "Datele complete ale prestatorului si beneficiarului",
            "Descrierea detaliata a serviciilor care fac obiectul contractului",
            "Durata contractului (determinata sau nedeterminata)",
            "Pretul si modalitatea de plata (suma fixa, per ora/zi/luna)",
            "Obligatiile prestatorului si ale beneficiarului",
            "Conditii de receptie a serviciilor",
            "Clauze de confidentialitate si proprietate intelectuala",
            "Conditii de reziliere si instanta competenta",
          ],
        },
        {
          title: "Cand ai nevoie de un contract de prestari servicii?",
          content: "Contractul de prestari servicii este necesar ori de cate ori colaborezi cu un freelancer, un PFA sau un SRL pentru executarea unor servicii specifice. Este diferit de contractul individual de munca — prestatorul nu este angajat, nu are program fix si nu se afla in subordinea beneficiarului. Este ideal pentru servicii de consultanta, dezvoltare software, design, marketing, contabilitate sau orice alta activitate prestata independent.",
        },
        {
          title: "Care este diferenta fata de contractul de munca?",
          content: "Contractul de prestari servicii se deosebeste de contractul de munca prin mai multe aspecte: prestatorul lucreaza independent (nu este subordonat), nu are program fix de lucru, isi stabileste singur metodele de lucru, factureaza serviciile prestate si isi gestioneaza singur obligatiile fiscale. Daca relatia dintre parti se aseamana cu un raport de munca (program fix, subordine, loc de munca stabilit), contractul poate fi recalificat de Inspectia Muncii ca raport de munca.",
        },
      ],
      faq: [
        {
          q: "Trebuie autentificat la notar contractul de prestari servicii?",
          a: "Nu, contractul de prestari servicii nu necesita autentificare notariala. Un contract semnat de ambele parti este suficient si valid din punct de vedere juridic.",
        },
        {
          q: "Cine plateste impozitele si contributiile?",
          a: "Daca prestatorul este PFA sau SRL, isi gestioneaza singur obligatiile fiscale. Daca prestatorul este persoana fizica, veniturile din prestari servicii se declara in Declaratia Unica si se datoreaza impozit pe venit si contributii sociale.",
        },
        {
          q: "Pot include o clauza de confidentialitate?",
          a: "Da, contractul poate include o clauza de confidentialitate (NDA) prin care ambele parti se obliga sa pastreze confidentialitatea informatiilor. Generatorul nostru include aceasta optiune.",
        },
      ],
    },
  },
  {
    id: "colaborare-pfa",
    category: "firme",
    slug: CONTRACT_SLUGS["colaborare-pfa"],
    title: "Contract Colaborare PFA",
    description: "Contract de colaborare PFA cu SRL sau persoana fizica, conform legislatiei in vigoare.",
    seoTitle: "Model Contract Colaborare PFA 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează contract de colaborare PFA în 2 minute, cu clauze de independență, NDA și non-concurență. PDF gratuit, fără cont.",
    seoIntro: "Generează un contract de colaborare PFA – model 2026 – în format PDF, conform legislației din România. Ideal pentru PFA-uri care colaborează cu SRL-uri sau persoane fizice. Formularul include datele PFA-ului și ale beneficiarului, obiectul colaborării, tariful, durata, clauze de confidențialitate, non-concurență și proprietate intelectuală. Descarci imediat, gratuit, fără cont.",
    choiceIntro: "Un contract de colaborare PFA fără clauza de independență riscă recalificarea de către Inspecția Muncii — cu consecințe fiscale majore. Generatorul nostru include automat clauzele obligatorii de independență, NDA și non-concurență — în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Clauze de independență anti-recalificare incluse",
    icon: "\uD83E\uDD1D",
    available: true,
    config: colaborarePFAConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un contract de colaborare PFA?",
          content: "Contractul de colaborare PFA este un acord prin care un PFA (Persoana Fizica Autorizata) se obliga sa presteze servicii in favoarea unui beneficiar (SRL sau persoana fizica), in mod independent. Spre deosebire de contractul de munca, PFA-ul isi organizeaza singur activitatea, programul de lucru si metodele de executie. Acest model de contract de colaborare PFA 2026 este actualizat conform legislatiei in vigoare si include clauze de independenta, confidentialitate si proprietate intelectuala.",
        },
        {
          title: "Care e diferenta dintre contract de colaborare PFA si contract de munca?",
          content: "Contractul de colaborare presupune independenta — PFA-ul isi organizeaza singur activitatea, nu are program fix si nu e subordonat beneficiarului. Contractul de munca presupune subordonare, program fix si drepturi specifice (concediu, preaviz conform Codului Muncii). Daca relatia dintre parti seamana cu un raport de munca (program fix, subordine, prezenta la sediu, utilizarea echipamentelor beneficiarului), Inspectia Muncii poate recalifica contractul.",
        },
        {
          title: "Ce clauze trebuie sa contina contractul?",
          content: [
            "Datele complete ale PFA-ului si ale beneficiarului",
            "Obiectul colaborarii — descrierea detaliata a serviciilor",
            "Tariful si modalitatea de plata",
            "Durata contractului (determinata sau nedeterminata)",
            "Clauza de independenta — cruciale pentru a evita recalificarea",
            "Clauza de confidentialitate (NDA)",
            "Proprietatea intelectuala — cui ii apartin rezultatele",
            "Conditii de reziliere si preaviz",
          ],
        },
        {
          title: "Cine plateste taxele si contributiile?",
          content: "PFA-ul este singur responsabil pentru plata impozitului pe venit, CAS si CASS. Beneficiarul plateste doar tariful convenit in contract. PFA-ul declara veniturile prin Declaratia Unica (formularul 212) si plateste contributiile sociale in functie de plafonul de venituri realizat.",
        },
      ],
      faq: [
        {
          q: "Poate Inspectia Muncii sa recalifice contractul?",
          a: "Da, daca relatia seamana cu un raport de munca (program fix, subordine, prezenta la sediu, utilizarea echipamentelor beneficiarului), Inspectia Muncii poate recalifica contractul cu consecinte fiscale semnificative.",
        },
        {
          q: "Trebuie autentificat la notar?",
          a: "Nu, contractul de colaborare nu necesita autentificare notariala. Un contract semnat de ambele parti este suficient.",
        },
        {
          q: "Pot include o clauza de confidentialitate?",
          a: "Da, generatorul nostru include optiunea de a adauga o clauza de confidentialitate (NDA) si o clauza de non-concurenta cu durata si domeniu personalizabil.",
        },
      ],
    },
  },
  {
    id: "notificare-reziliere",
    category: "pf",
    slug: CONTRACT_SLUGS["notificare-reziliere"],
    title: "Notificare Reziliere",
    description: "Notificare de reziliere a contractului de inchiriere, gata de trimis.",
    seoTitle: "Model Notificare Reziliere Închiriere 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează notificare de reziliere a contractului de închiriere în 2 minute. PDF formal, gata de trimis prin scrisoare recomandată.",
    seoIntro: "Generează o notificare de reziliere a contractului de închiriere – model 2026 – în format PDF, formală și gata de trimis. Completezi datele expeditorului, destinatarului, motivul rezilierii și termenul de preaviz. Descarci documentul imediat, gratuit, fără cont. Ideal pentru proprietari sau chiriași care doresc să rezilieze contractul conform legii.",
    choiceIntro: "O notificare de reziliere incompletă sau cu date greșite poate fi contestată în instanță. Generatorul nostru completează automat referințele la contract, calculează termenul de preaviz și generează un document formal — în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Termen de preaviz calculat automat conform legii",
    icon: "\uD83D\uDCE8",
    available: true,
    showOnHomepage: true,
    config: notificareReziliereConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este o notificare de reziliere?",
          content: "Notificarea de reziliere este un document formal prin care una dintre partile contractului de inchiriere (proprietarul sau chiriasul) comunica celeilalte parti decizia de a inceta contractul. Notificarea trebuie sa contina motivul rezilierii, termenul de preaviz acordat si data de la care contractul inceteaza.",
        },
        {
          title: "Cum se trimite notificarea de reziliere?",
          content: "Notificarea trebuie trimisa prin scrisoare recomandata cu confirmare de primire sau inmanata personal sub semnatura. Aceasta metoda asigura dovada ca destinatarul a primit notificarea, ceea ce este esential in caz de litigiu.",
        },
        {
          title: "Ce termen de preaviz trebuie acordat?",
          content: "Termenul de preaviz depinde de tipul contractului. Pentru contractele pe durata nedeterminata, chiriasul trebuie sa acorde un preaviz rezonabil (de obicei 30 de zile), iar proprietarul minimum 60 de zile. Pentru contractele pe durata determinata, chiriasul poate denunta cu 60 de zile preaviz. Verificati clauzele din contractul dvs. pentru termene specifice.",
        },
      ],
      faq: [
        {
          q: "Pot rezilia contractul fara motiv?",
          a: "Da, in cazul contractelor pe durata nedeterminata, oricare dintre parti poate denunta unilateral contractul cu respectarea termenului de preaviz. Pentru contractele pe durata determinata, rezilierea fara motiv este posibila doar daca aceasta posibilitate a fost prevazuta in contract.",
        },
        {
          q: "Ce se intampla daca nu trimit notificarea?",
          a: "Fara o notificare formala, rezilierea poate fi contestata. Notificarea scrisa constituie dovada ca ati respectat procedura legala si termenul de preaviz.",
        },
        {
          q: "Trebuie sa inregistrez rezilierea la ANAF?",
          a: "Da, proprietarul are obligatia de a depune formularul C168 la ANAF, bifand casuta Incetare, in termen de 30 de zile de la incetarea contractului.",
        },
      ],
    },
  },
  {
    id: "nda",
    category: "firme",
    slug: CONTRACT_SLUGS["nda"],
    title: "NDA — Acord de Confidentialitate",
    description: "Acord de confidentialitate (NDA) unilateral sau bilateral, conform legii romane.",
    seoTitle: "Model NDA — Acord de Confidențialitate 2026 — Generator PDF Gratuit | ContractGen",
    seoDescription: "Generează NDA (acord de confidențialitate) personalizat în 2 minute — unilateral sau bilateral. PDF gratuit, conform legii române.",
    seoIntro: "Generează un acord de confidențialitate (NDA) – model 2026 – în format PDF, conform legislației din România. Alege între NDA unilateral sau bilateral, completează datele părților, definește informațiile confidențiale, durata și penalitățile. Descarci imediat, gratuit, fără cont.",
    choiceIntro: "Un NDA generic descărcat de pe alte site-uri poate să nu acopere toate categoriile de informații confidențiale relevante pentru cazul tău. Generatorul nostru te ghidează pas cu pas — alegi între unilateral și bilateral, definești categoriile și penalitățile — în 2 minute. Sau descarci modelul PDF gol.",
    bonusBenefit: "Categorii de informații confidențiale personalizabile",
    icon: "\uD83D\uDD12",
    available: true,
    config: ndaConfig,
    seoContent: {
      sections: [
        {
          title: "Ce este un NDA?",
          content: "NDA (Non-Disclosure Agreement) sau Acordul de Confidentialitate este un contract prin care una sau ambele parti se obliga sa nu divulge informatiile confidentiale primite. Este esential inainte de discutii de afaceri, colaborari sau parteneriate.",
        },
        {
          title: "Care e diferenta dintre NDA unilateral si bilateral?",
          content: "NDA unilateral protejeaza informatiile unei singure parti (de exemplu, o companie dezvaluie informatii unui freelancer). NDA bilateral protejeaza informatiile ambelor parti (de exemplu, doua companii care negociaza un parteneriat).",
        },
        {
          title: "Trebuie autentificat la notar?",
          content: "Nu, un NDA semnat de ambele parti este valid fara autentificare notariala.",
        },
        {
          title: "Ce se intampla daca cineva incalca NDA-ul?",
          content: "Partea prejudiciata poate solicita daune-interese in instanta. Daca NDA-ul include o clauza de penalitati, suma poate fi ceruta direct, fara a fi nevoie sa dovedesti prejudiciul exact.",
        },
        {
          title: "Cat ar trebui sa dureze un NDA?",
          content: "Depinde de natura informatiilor. Pentru colaborari comerciale, 2-3 ani este standard. Pentru secrete comerciale sau tehnologice, 5 ani sau mai mult. Informatiile de tip trade secret pot fi protejate pe durata nelimitata.",
        },
      ],
      faq: [
        {
          q: "Ce este un NDA?",
          a: "NDA (Non-Disclosure Agreement) sau Acordul de Confidentialitate este un contract prin care una sau ambele parti se obliga sa nu divulge informatiile confidentiale primite. Este esential inainte de discutii de afaceri, colaborari sau parteneriate.",
        },
        {
          q: "Care e diferenta dintre NDA unilateral si bilateral?",
          a: "NDA unilateral protejeaza informatiile unei singure parti (de exemplu, o companie dezvaluie informatii unui freelancer). NDA bilateral protejeaza informatiile ambelor parti (de exemplu, doua companii care negociaza un parteneriat).",
        },
        {
          q: "Trebuie autentificat la notar?",
          a: "Nu, un NDA semnat de ambele parti este valid fara autentificare notariala. Documentul are valoare juridica prin simpla semnare de catre parti.",
        },
        {
          q: "Ce se intampla daca cineva incalca NDA-ul?",
          a: "Partea prejudiciata poate solicita daune-interese in instanta. Daca NDA-ul include o clauza de penalitati, suma poate fi ceruta direct, fara a fi nevoie sa dovedesti prejudiciul exact.",
        },
        {
          q: "Cat ar trebui sa dureze un NDA?",
          a: "Depinde de natura informatiilor. Pentru colaborari comerciale, 2-3 ani este standard. Pentru secrete comerciale sau tehnologice, 5 ani sau mai mult. Informatiile de tip trade secret pot fi protejate pe durata nelimitata.",
        },
      ],
    },
  },
];

// Blank fillable model PDFs prebuilt by scripts/generate-model-pdfs.js.
// Served from public/models/ at runtime.
const MODEL_PDFS = {
  "inchiriere": "model-contract-inchiriere-pdf.pdf",
  "act-aditional-inchiriere": "model-act-aditional-inchiriere-pdf.pdf",
  "imprumut": "model-contract-imprumut-pdf.pdf",
  "vanzare-auto": "model-contract-vanzare-auto-pdf.pdf",
  "comodat": "model-contract-comodat-pdf.pdf",
  "prestari-servicii": "model-contract-prestari-servicii-pdf.pdf",
  "colaborare-pfa": "model-contract-colaborare-pfa-pdf.pdf",
  "notificare-reziliere": "model-notificare-reziliere-pdf.pdf",
  "nda": "model-acord-confidentialitate-nda-pdf.pdf",
};

// Map contract id -> dedicated PDF landing page slug (only "live" landings).
// Used by the bottom-of-page PDF text link so PDF-seekers land on a content
// page instead of triggering a direct download from the wizard page.
const MODEL_LANDING_SLUGS = {
  "inchiriere": "model-contract-inchiriere-pdf",
};

function ContractPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardStarted, setWizardStarted] = useState(false);
  const handleStepChange = useCallback((step) => {
    setWizardStep(step);
    if (step > 0) setWizardStarted(true);
  }, []);

  const contract = CONTRACT_TYPES.find((c) => c.slug === slug);

  useEffect(() => {
    if (contract?.available && MODEL_PDFS[contract.id] && !wizardStarted) {
      trackFlowChoiceViewed(contract.id);
    }
  }, [contract?.id, contract?.available, wizardStarted]);

  if (!contract || !contract.config) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", fontFamily: "'DM Sans', sans-serif" }}>
        <h1>Contract negasit</h1>
        <p>Acest tip de contract nu exista.</p>
        <Link to="/" style={{ color: "#c45d3e", fontWeight: 600 }}>Inapoi la pagina principala</Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={contract.seoTitle || `${contract.config.title} - Generator Gratuit | ContractGen`}
        description={contract.seoDescription}
        canonical={`https://generator-contracte.com/${contract.slug}`}
      />
      <ContractLayout
        title={contract.config.title}
        subtitle={contract.config.subtitle}
      >
        {MODEL_PDFS[contract.id] && !wizardStarted ? (
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px 24px 0", fontFamily: "'DM Sans', sans-serif" }}>
            {/* Intro card (kept for SEO context) */}
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: isMobile ? "20px" : "24px 28px",
              border: "1px solid #e8e0d4",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              marginBottom: "20px",
            }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: isMobile ? "20px" : "22px",
                fontWeight: 700,
                color: "#2d2418",
                margin: "0 0 10px 0",
              }}>
                {contract.icon} {contract.title} — Model 2026
              </h2>
              <p style={{ fontSize: "14px", color: "#6b6156", margin: 0, lineHeight: 1.7 }}>
                Generează contractul personalizat în 2 minute, cu CNP, IBAN și sume validate automat.
              </p>
            </div>

            {/* Single centered wizard CTA — "GENERATOR OFICIAL" */}
            <a
              href="#wizard"
              onClick={(e) => {
                e.preventDefault();
                setWizardStarted(true);
                trackFlowChoiceClicked(contract.id, "online_wizard");
                requestAnimationFrame(() => {
                  document.getElementById("wizard")?.scrollIntoView({ behavior: "smooth" });
                });
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: isMobile ? "28px 20px" : "32px 28px",
                border: "2px solid #c45d3e",
                boxShadow: "0 4px 16px rgba(196,93,62,0.12)",
                textDecoration: "none",
                color: "inherit",
                position: "relative",
                transition: "transform 0.15s ease",
                textAlign: "center",
                alignItems: "center",
                marginBottom: "20px",
              }}
              onMouseEnter={(ev) => { ev.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(ev) => { ev.currentTarget.style.transform = "translateY(0)"; }}
            >
              <span style={{
                position: "absolute",
                top: "-10px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: "10px",
                letterSpacing: "0.5px",
                whiteSpace: "nowrap",
              }}>GENERATOR OFICIAL</span>
              <div style={{ fontSize: "32px", marginBottom: "10px" }}>{"✨"}</div>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: 700,
                color: "#2d2418",
                margin: "0 0 8px 0",
              }}>
                Generează contractul personalizat în 2 minute
              </h3>
              <p style={{
                fontSize: "14px",
                color: "#6b6156",
                margin: "0 0 20px 0",
                lineHeight: 1.6,
                maxWidth: "520px",
              }}>
                Completezi datele într-un formular ghidat. PDF final gata de semnat, cu CNP, IBAN și sume validate automat — gratuit, fără cont.
              </p>
              <span style={{
                display: "inline-block",
                padding: "12px 28px",
                background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                color: "#fff",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: 700,
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(196,93,62,0.25)",
                width: isMobile ? "100%" : "auto",
                boxSizing: "border-box",
              }}>
                Începe formularul →
              </span>
            </a>

            {/* Trust signals */}
            <div style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: "wrap",
              gap: isMobile ? "6px" : "8px 24px",
              justifyContent: isMobile ? "flex-start" : "center",
              marginBottom: "16px",
            }}>
              {[
                "Peste 100 de contracte generate",
                "Actualizat 2026, conform Codului Civil",
                "Fără cont, fără date stocate pe serverele noastre",
                "Fără greșeli — CNP, IBAN și sume validate automat",
              ].map((text) => (
                <span key={text} style={{ fontSize: "12px", color: "#6b6156" }}>
                  ✓ {text}
                </span>
              ))}
            </div>

            {/* SEO long description — at bottom for mobile, visible for Google */}
            {!isMobile && contract.seoIntro && (
              <div style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "24px 28px",
                border: "1px solid #e8e0d4",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}>
                <p style={{ fontSize: "14px", color: "#6b6156", margin: 0, lineHeight: 1.7 }}>
                  {contract.seoIntro}
                </p>
              </div>
            )}
          </div>
        ) : !MODEL_PDFS[contract.id] && (
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "32px 24px 0" }}>
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "24px 28px",
              border: "1px solid #e8e0d4",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "20px",
                fontWeight: 700,
                color: "#2d2418",
                margin: "0 0 10px 0",
              }}>
                {contract.icon} {contract.title} — Generator Online Gratuit
              </h2>
              <p style={{ fontSize: "14px", color: "#6b6156", margin: 0, lineHeight: 1.7 }}>
                {contract.seoIntro}
              </p>
            </div>
          </div>
        )}
        {(wizardStarted || !MODEL_PDFS[contract.id]) && (
          <div id="wizard" style={{ scrollMarginTop: "16px" }}>
            <StepWizard
              key={slug}
              config={contract.config}
              contractType={contract.id}
              modelPdf={MODEL_PDFS[contract.id]}
              onBack={() => navigate("/")}
              onStepChange={handleStepChange}
            />
          </div>
        )}
        {contract.seoContent && wizardStep === 0 && (
          <section style={{ maxWidth: "800px", margin: "0 auto", padding: "40px 24px 0" }}>
            <div style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "40px 32px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              border: "1px solid #e8e0d4",
            }}>
              {contract.seoContent.sections.map((section, i) => (
                <div key={i} style={{ marginBottom: i < contract.seoContent.sections.length - 1 ? "28px" : "0" }}>
                  <h2 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#2d2418",
                    margin: "0 0 12px 0",
                  }}>
                    {section.title}
                  </h2>
                  {Array.isArray(section.content) ? (
                    <ul style={{
                      margin: 0,
                      paddingLeft: "20px",
                      listStyleType: "disc",
                    }}>
                      {section.content.map((item, j) => (
                        <li key={j} style={{
                          fontSize: "14px",
                          color: "#6b6156",
                          lineHeight: 1.7,
                          marginBottom: "4px",
                        }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{
                      fontSize: "14px",
                      color: "#6b6156",
                      margin: 0,
                      lineHeight: 1.7,
                    }}>
                      {section.content}
                    </p>
                  )}
                </div>
              ))}

              {contract.seoContent.faq.length > 0 && (
                <div style={{ marginTop: "36px" }}>
                  <h2 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#2d2418",
                    margin: "0 0 20px 0",
                  }}>
                    Intrebari frecvente
                  </h2>
                  {contract.seoContent.faq.map((item, i) => (
                    <div key={i} style={{
                      padding: "16px 0",
                      borderBottom: i < contract.seoContent.faq.length - 1 ? "1px solid #e8e0d4" : "none",
                    }}>
                      <h3 style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#2d2418",
                        margin: "0 0 8px 0",
                      }}>
                        {item.q}
                      </h3>
                      <p style={{
                        fontSize: "14px",
                        color: "#6b6156",
                        margin: 0,
                        lineHeight: 1.6,
                      }}>
                        {item.a}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
        {MODEL_PDFS[contract.id] && !wizardStarted && (
          <div style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "32px 24px 40px",
            textAlign: "center",
          }}>
            {MODEL_LANDING_SLUGS[contract.id] ? (
              <Link
                to={`/${MODEL_LANDING_SLUGS[contract.id]}`}
                onClick={() => trackFlowChoiceClicked(contract.id, "model_pdf")}
                style={{
                  fontSize: "13px",
                  color: "#8a7e70",
                  textDecoration: "none",
                  borderBottom: "1px dotted #b8a890",
                  paddingBottom: "1px",
                }}
              >
                Cauți doar modelul PDF gol? Descarcă modelul de contract PDF →
              </Link>
            ) : (
              <a
                href={`/models/${MODEL_PDFS[contract.id]}`}
                download={MODEL_PDFS[contract.id]}
                onClick={() => {
                  trackFlowChoiceClicked(contract.id, "model_pdf");
                  trackModelPdfDownload(contract.id, "choice_screen_text_link");
                }}
                style={{
                  fontSize: "13px",
                  color: "#8a7e70",
                  textDecoration: "none",
                  borderBottom: "1px dotted #b8a890",
                  paddingBottom: "1px",
                }}
              >
                Cauți doar modelul PDF gol? Descarcă modelul de contract PDF ↓
              </a>
            )}
          </div>
        )}
      </ContractLayout>
    </>
  );
}

function Homepage() {
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(165deg, #f5f0e8 0%, #ede6da 30%, #f0e8d8 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <SEOHead
        title="Generator Contracte Online Gratuit - Contract Inchiriere, Vanzare Auto, Comodat | ContractGen"
        description="Genereaza contracte online gratuit: contract de inchiriere, contract de vanzare-cumparare auto, contract de imprumut, contract de comodat. Fara cont, fara costuri, conform legislatiei din Romania."
        canonical="https://generator-contracte.com/"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700;800&display=swap"
        rel="stylesheet"
      />

      <NavBar />

      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #2d2418 0%, #4a3520 50%, #3d2a18 100%)",
          padding: isMobile ? "40px 16px" : "60px 24px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "radial-gradient(circle at 20% 50%, rgba(196, 93, 62, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(196, 93, 62, 0.1) 0%, transparent 40%)",
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "inline-block",
              padding: "6px 16px",
              backgroundColor: "rgba(196, 93, 62, 0.2)",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#e8a88a",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "16px",
            }}
          >
            Generator gratuit de contracte
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(32px, 5vw, 52px)",
              fontWeight: 800,
              color: "#fdfbf7",
              margin: "0 0 12px 0",
              letterSpacing: "-0.02em",
            }}
          >
            ContractGen
          </h1>
          <p style={{ fontSize: "17px", color: "#b8a890", maxWidth: "550px", margin: "0 auto", lineHeight: 1.6 }}>
            Alege tipul de contract si genereaza un document complet, valid conform legislatiei din Romania.
          </p>
        </div>
      </div>

      {/* Contract cards */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "40px 24px" }}>
        {[
          { category: "pf", title: "Contracte Persoane Fizice" },
          { category: "firme", title: "Contracte Firme & Freelanceri" },
        ].map((group) => {
          const contracts = CONTRACT_TYPES.filter((ct) => ct.category === group.category && ct.showOnHomepage !== false);
          if (contracts.length === 0) return null;
          return (
            <div key={group.category} style={{ marginBottom: "40px" }}>
              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(20px, 3vw, 26px)",
                fontWeight: 800,
                color: "#2d2418",
                margin: "0 0 20px 0",
                textAlign: "center",
              }}>
                {group.title}
              </h2>
              <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? "12px" : "20px", justifyContent: "center" }}>
                {contracts.map((ct) => (
                  <Link
                    key={ct.id}
                    to={ct.available ? `/${ct.slug}` : "#"}
                    onClick={() => {
                      // select_contract only fires on card click, not on page
                      // view. Use page_view for landing analytics.
                      if (ct.available) {
                        trackSelectContract(ct.id);
                      }
                    }}
                    style={{
                      flex: isMobile ? "1 1 100%" : "1 1 260px",
                      ...(isMobile ? {} : { maxWidth: "320px" }),
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      padding: isMobile ? "24px 20px" : "32px 28px",
                      boxShadow: ct.available
                        ? "0 4px 24px rgba(0,0,0,0.06)"
                        : "0 2px 12px rgba(0,0,0,0.03)",
                      border: "1px solid #e8e0d4",
                      cursor: ct.available ? "pointer" : "default",
                      opacity: ct.available ? 1 : 0.55,
                      transition: "all 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                    }}
                    onMouseEnter={(e) => {
                      if (ct.available) {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.10)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (ct.available) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.06)";
                      }
                    }}
                  >
                    {!ct.available && (
                      <div style={{
                        position: "absolute", top: "16px", right: "-28px",
                        transform: "rotate(45deg)",
                        backgroundColor: "#e8e0d4", padding: "4px 40px",
                        fontSize: "10px", fontWeight: 700, color: "#8a7e70",
                        letterSpacing: "0.1em", textTransform: "uppercase",
                      }}>
                        In curand
                      </div>
                    )}
                    <div style={{ fontSize: isMobile ? "36px" : "48px", marginBottom: "16px" }}>{ct.icon}</div>
                    <h3 style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "20px", fontWeight: 700, color: "#2d2418",
                      margin: "0 0 8px 0",
                    }}>
                      {ct.title}
                    </h3>
                    <p style={{
                      fontSize: "14px", color: "#8a7e70", margin: 0,
                      lineHeight: 1.5,
                    }}>
                      {ct.description}
                    </p>
                    {ct.available && (
                      <div style={{
                        marginTop: "20px", padding: "10px 0",
                        textAlign: "center", borderTop: "1px solid #e8e0d4",
                      }}>
                        <span style={{
                          fontSize: "14px", fontWeight: 600, color: "#c45d3e",
                          letterSpacing: "0.02em",
                        }}>
                          Incepe acum →
                        </span>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Blog link */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "0 24px 32px", textAlign: "center" }}>
        <Link
          to="/blog"
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#c45d3e",
            textDecoration: "none",
            transition: "opacity 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.8"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
        >
          Citeste ghidurile noastre despre contracte →
        </Link>
      </div>

      {/* SEO Content Section */}
      <section style={{ maxWidth: "900px", margin: "0 auto", padding: "0 24px 40px" }}>
        <div style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: isMobile ? "32px 20px" : "48px 40px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          border: "1px solid #e8e0d4",
        }}>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(24px, 4vw, 32px)",
            fontWeight: 800,
            color: "#2d2418",
            margin: "0 0 16px 0",
            textAlign: "center",
          }}>
            Genereaza contracte online, gratuit
          </h2>
          <p style={{
            fontSize: "15px",
            color: "#6b6156",
            lineHeight: 1.7,
            maxWidth: "700px",
            margin: "0 auto 32px",
            textAlign: "center",
          }}>
            ContractGen este un generator de contracte online gratuit, creat pentru persoane fizice din Romania.
            Completeaza formularul pas cu pas si descarca contractul in format PDF, gata de printat si semnat.
            Nu este nevoie de cont si nu stocam datele tale personale.
          </p>

          {[
            { category: "pf", title: "Contracte Persoane Fizice" },
            { category: "firme", title: "Contracte Firme & Freelanceri" },
          ].map((group) => {
            const contracts = CONTRACT_TYPES.filter((ct) => ct.category === group.category && ct.showOnHomepage !== false);
            if (contracts.length === 0) return null;
            return (
              <div key={group.category} style={{ marginBottom: "32px" }}>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#2d2418",
                  margin: "0 0 16px 0",
                }}>
                  {group.title}
                </h3>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "24px",
                }}>
                  {contracts.map((ct) => (
                    <div key={ct.id}>
                      <h4 style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#2d2418",
                        margin: "0 0 6px 0",
                      }}>
                        {ct.icon} {ct.title}
                      </h4>
                      <p style={{ fontSize: "14px", color: "#8a7e70", margin: 0, lineHeight: 1.6 }}>
                        {ct.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* FAQ */}
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(20px, 3vw, 26px)",
            fontWeight: 800,
            color: "#2d2418",
            margin: "0 0 24px 0",
            textAlign: "center",
          }}>
            Intrebari frecvente
          </h2>

          {[
            {
              q: "Este gratuit?",
              a: "Da, toate contractele generate pe ContractGen sunt 100% gratuite. Nu exista costuri ascunse si nu trebuie sa platesti pentru descarcare.",
            },
            {
              q: "Trebuie sa imi fac cont?",
              a: "Nu, nu este nevoie de cont. Completezi formularul, descarci contractul in format PDF si gata. Nu stocam datele tale personale.",
            },
            {
              q: "Contractele sunt valide juridic?",
              a: "Contractele generate sunt modele orientative, conforme cu legislatia din Romania (Codul Civil). Pentru situatii complexe sau litigii, recomandam consultarea unui avocat.",
            },
            {
              q: "Ce tipuri de contracte pot genera?",
              a: "Momentan poti genera: contract de inchiriere, act aditional inchiriere, notificare de reziliere, contract de imprumut, contract de vanzare-cumparare auto, contract de comodat, contract de prestari servicii si contract de colaborare PFA.",
            },
          ].map((item, i) => (
            <div key={i} style={{
              padding: "16px 0",
              borderBottom: i < 3 ? "1px solid #e8e0d4" : "none",
            }}>
              <h3 style={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#2d2418",
                margin: "0 0 8px 0",
              }}>
                {item.q}
              </h3>
              <p style={{
                fontSize: "14px",
                color: "#6b6156",
                margin: 0,
                lineHeight: 1.6,
              }}>
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "0 24px 40px" }}>
        <div
          style={{
            padding: "20px 24px",
            backgroundColor: "rgba(196, 93, 62, 0.06)",
            borderRadius: "12px",
            border: "1px solid rgba(196, 93, 62, 0.12)",
          }}
        >
          <p style={{ fontSize: "13px", color: "#8a7e70", margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: "#4a3f35" }}>Disclaimer:</strong> Documentele de pe acest site sunt modele orientative și nu constituie consultanță juridică. Utilizarea lor se face pe propria răspundere. Pentru situații particulare sau complexe, se recomandă consultarea unui avocat.
          </p>
        </div>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/model-contract-inchiriere-pdf" element={<ModelLandingPage />} />
      <Route path="/:slug" element={<ContractPage />} />
    </Routes>
    </>
  );
}
